import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import CompanionCard from "@/components/CompanionCard";
import { useI18n } from "@/lib/i18n";

export default function Companions() {
  const { t } = useI18n();
  const [companions, setCompanions] = useState([]);
  const [options, setOptions] = useState({ services: [], nationalities: [], hair: [] });
  const [filters, setFilters] = useState({
    service: "",
    nationality: "",
    hair: "",
    min_age: "",
    max_age: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/filters/options").then((r) => setOptions(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== "" && v !== null)
    );
    api.get("/companions", { params }).then((r) => {
      setCompanions(r.data || []);
      setLoading(false);
    });
  }, [filters]);

  const reset = () =>
    setFilters({ service: "", nationality: "", hair: "", min_age: "", max_age: "" });

  return (
    <div data-testid="companions-page" className="pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-16">
          <div className="label-luxury mb-4">{t("directory")}</div>
          <h1 className="font-serif-display text-5xl md:text-7xl font-light tracking-tight">
            {t("our_companions_1")} <span className="italic gold-text">{t("our_companions_2")}</span>
          </h1>
          <p className="text-white/60 mt-6 max-w-xl">
            {t("companions_intro")}
          </p>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-10">
          <aside className="space-y-8" data-testid="filters-sidebar">
            <div>
              <div className="label-luxury mb-4">{t("filter_service")}</div>
              <select
                data-testid="filter-service"
                className="luxury-select"
                value={filters.service}
                onChange={(e) => setFilters({ ...filters, service: e.target.value })}
              >
                <option value="">{t("filter_all_services")}</option>
                {options.services.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-luxury mb-4">{t("filter_nationality")}</div>
              <select
                data-testid="filter-nationality"
                className="luxury-select"
                value={filters.nationality}
                onChange={(e) => setFilters({ ...filters, nationality: e.target.value })}
              >
                <option value="">{t("filter_all_countries")}</option>
                {options.nationalities.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-luxury mb-4">{t("filter_hair")}</div>
              <select
                data-testid="filter-hair"
                className="luxury-select"
                value={filters.hair}
                onChange={(e) => setFilters({ ...filters, hair: e.target.value })}
              >
                <option value="">{t("filter_all_colours")}</option>
                {options.hair.map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label-luxury mb-4">{t("filter_age")}</div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="filter-min-age"
                  type="number"
                  className="luxury-input"
                  placeholder={t("filter_min")}
                  value={filters.min_age}
                  onChange={(e) => setFilters({ ...filters, min_age: e.target.value })}
                />
                <input
                  data-testid="filter-max-age"
                  type="number"
                  className="luxury-input"
                  placeholder={t("filter_max")}
                  value={filters.max_age}
                  onChange={(e) => setFilters({ ...filters, max_age: e.target.value })}
                />
              </div>
            </div>
            <button
              data-testid="filter-reset"
              onClick={reset}
              className="btn-gold w-full"
            >
              {t("filter_reset")}
            </button>
            <div className="text-white/40 text-xs tracking-wider pt-4 border-t border-white/5">
              {companions.length} {companions.length === 1 ? t("companion_count_one") : t("companion_count_many")}
            </div>
          </aside>

          <main>
            {loading ? (
              <div className="text-center text-white/40 py-20 label-luxury">{t("loading")}</div>
            ) : companions.length === 0 ? (
              <div className="text-center text-white/40 py-20">
                <div className="label-luxury mb-3">{t("no_matches")}</div>
                <p>{t("no_matches_text")}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-8 md:gap-12 max-w-2xl mx-auto">
                {companions.map((c, i) => (
                  <CompanionCard key={c.id} c={c} index={i} />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
